import { supabase } from '../lib/supabase'

/**
 * Unattended workers, and whether they are still running.
 *
 * Portal OS leans on scheduled Claude tasks for anything that has to happen
 * without a person present — the Descript transcript queue, the calendar
 * worker, the morning news round-up — and, since 22 Sep 2026, on Supabase edge
 * functions for the jobs that never needed a model in the first place. They
 * all share one failure mode, and it is the quiet one: the task stops, nothing
 * errors, and the studio carries on believing it is running. Weeks later the
 * calendar is wrong and the news tab is a museum.
 *
 * `transcript_jobs` can be judged without any help from the worker, because a
 * request sits there ageing whether or not anything picks it up (see
 * `utils/transcriptJobs.ts`). The others leave nothing behind when they work
 * and nothing behind when they stop. The only way to tell those apart is for
 * the worker to say so on every run — which is what `worker_heartbeats` is.
 *
 * The obvious objection applies to any heartbeat: it is written by the thing
 * it reports on. That is fine here, because the claim being tested is not
 * "did this run succeed" but "is this still running at all" — and a stopped
 * task cannot write a fresh timestamp. Read-only from the app; the workers
 * write it server-side.
 *
 * The studio's name for each worker, and what stops working when it does,
 * live in `utils/workerNames.ts` — split out 6 Oct 2026 so that registering a
 * new worker does not mean editing this file. They are re-exported here so
 * every caller still has one import.
 */

export { workerConsequence, workerName } from './workerNames'

export type WorkerStatus = 'ok' | 'error'

export interface WorkerHeartbeat {
  worker: string
  /** Epoch ms of the worker's last completed run. */
  lastRunAt: number
  status: WorkerStatus
  /** One line the worker left behind — a summary, or the reason it failed. */
  detail?: string
  /** How often the worker is scheduled to run. */
  expectedIntervalMinutes: number
  /**
   * The longest silence the schedule legitimately allows — an overnight break,
   * a weekend. Undefined for a worker that ticks at a constant interval.
   */
  maxGapMinutes?: number
}

/**
 * How many scheduled runs a worker may miss before the dashboard says so.
 *
 * Two, not one: a single skipped run is normal — a slow queue, a restart, a
 * schedule that drifts by a few minutes past its window. Reporting that would
 * teach everyone to ignore the line, which costs more than the delay does.
 */
export const MISSED_RUNS_BEFORE_LATE = 2

/**
 * How long this worker may stay quiet before something is actually wrong.
 *
 * Interval × missed runs is the right answer only for a worker that ticks
 * round the clock. The calendar sync does not: it runs a few times between
 * 07:26 and 22:26 Dubai and then deliberately sleeps for nine hours. Judged on
 * its three-hour interval alone it is "four missed runs late" every morning
 * before breakfast — an alarm that is wrong every single day, which is worse
 * than no alarm, because it trains you to ignore the one morning it is right.
 *
 * So a worker may declare the longest silence its own schedule builds in, and
 * the allowance becomes that gap plus one interval: long enough to sit through
 * the night, short enough that a genuinely dead worker is still called out
 * within half a day rather than at the next nightfall.
 *
 * A worker on a cron with no night off — the mirror, every fifteen minutes —
 * leaves `maxGapMinutes` null and is judged on its interval alone.
 */
export function lateAfterMinutes(beat: WorkerHeartbeat): number {
  const paced = beat.expectedIntervalMinutes * MISSED_RUNS_BEFORE_LATE
  const quiet = beat.maxGapMinutes ? beat.maxGapMinutes + beat.expectedIntervalMinutes : 0
  return Math.max(paced, quiet)
}

/** True when a worker has been quiet for longer than its own schedule allows. */
export function isWorkerLate(beat: WorkerHeartbeat, now: number = Date.now()): boolean {
  return now - beat.lastRunAt > lateAfterMinutes(beat) * 60_000
}

/** "Last ran 3 days ago" — the fact that matters most on the row. */
export function lastRunLabel(beat: WorkerHeartbeat, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - beat.lastRunAt) / 60_000))
  if (minutes < 90) return `Last ran ${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 36) return `Last ran ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  const days = Math.round(hours / 24)
  return `Last ran ${days} ${days === 1 ? 'day' : 'days'} ago`
}

/**
 * Every worker that is either late or reporting a failure.
 *
 * A worker with no row at all is invisible here, deliberately: this answers
 * "has something that was running stopped", not "has something that was never
 * set up been set up". The second question has no honest answer from data —
 * an empty table is indistinguishable from a studio that runs no workers.
 */
export async function fetchUnhealthyWorkers(
  now: number = Date.now(),
): Promise<WorkerHeartbeat[]> {
  if (!supabase) return []

  const { data, error } = await supabase
    .from('worker_heartbeats')
    .select('worker, last_run_at, status, detail, expected_interval_minutes, max_gap_minutes')

  if (error) {
    console.warn(`[supabase] worker heartbeat lookup failed: ${error.message}`)
    return []
  }

  return (data ?? [])
    .map((row) => ({
      worker: String(row.worker ?? ''),
      lastRunAt: new Date(String(row.last_run_at ?? '')).getTime() || 0,
      status: (row.status as WorkerStatus) ?? 'ok',
      detail: (row.detail as string | null) ?? undefined,
      expectedIntervalMinutes: Number(row.expected_interval_minutes) || 60,
      maxGapMinutes: Number(row.max_gap_minutes) || undefined,
    }))
    .filter((beat) => beat.status === 'error' || isWorkerLate(beat, now))
}
